import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, Validators } from '@angular/forms';
import { OnboardingPageService } from '../../onboarding-page/onboarding-page.service';
import { PanService } from '../../onboarding-page/pan/pan.service';
import { ToastMessage } from '../../toast-message/toast-message.service';

@Component({
  selector: 'app-fleet-onboarding-details',
  template: require('./fleet-onboarding-details.component.html'),
  providers: [OnboardingPageService]
})
export class FleetOnboardingDetailsComponent implements OnInit, OnDestroy {
  overHeadPanelTitle = 'Fleet Onboarding - Details';

  fleetId: string = '';
  dob: number | null = null;
  mobile: string = '';
  status: string = '';
  riderId: string = '';

  name: string = '';
  gstNumber: string = '';
  emailsText: string = '';
  type: string = '';
  modeId: string = '';
  cityId: string = '';

  citiesList: any[] = [];

  loading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';

  activeSection: 'info' | 'documents' = 'info';
  // Marks whether Info was successfully saved to backend
  private infoSaved: boolean = false;
  
  // Using existing app-pan component for PAN UI/validation
  nameFormControl = new FormControl('', [Validators.required]);
  gstFormControl = new FormControl('', [Validators.required]);
  emailsFormControl = new FormControl('', [Validators.required]);
  typeFormControl = new FormControl('', [Validators.required]);
  modeFormControl = new FormControl('', [Validators.required]);
  cityFormControl = new FormControl('', [Validators.required]);

  modeOptions: Array<{ id: string, name: string }> = [
    { id: 'Auto', name: 'Auto' },
    { id: 'Car', name: 'Car' },
    { id: 'Bike', name: 'Bike' }
  ];

  // Document status for PAN; values: 'approved' | 'processing' | 'rejected' | ''
  documentsStatus: string = '';

  constructor(private route: ActivatedRoute, private router: Router, private onboardingService: OnboardingPageService, private panService: PanService, private toastMessage: ToastMessage) {}

  ngOnInit() {
    const params = this.route.snapshot.queryParamMap;
    this.fleetId = params.get('fleetId') || params.get('_id') || '';
    const dobParam = params.get('dob');
    this.dob = dobParam ? Number(dobParam) : null;
    this.mobile = params.get('mobile') || '';
    this.status = String(params.get('status') || '').trim().toLowerCase();
    this.riderId = params.get('riderId') || '';
    if (!this.riderId && this.fleetId) {
      this.riderId = this.fleetId;
    }
    const modeIdParam = params.get('modeId');
    const modeNameParam = params.get('mode');
    if (modeIdParam) {
      this.modeId = modeIdParam;
      // Seed a placeholder so the selection shows even before options load
      const placeholderName = modeNameParam || 'Selected mode';
      this.modeOptions = [{ id: modeIdParam, name: placeholderName }];
    }
    const typeParam = params.get('type');
    if (typeParam) { this.type = typeParam; }

    // Auto-populate incoming values
    const nameParam = params.get('name');
    if (nameParam) { this.name = nameParam; }
    const gstParam = params.get('gstNumber');
    if (gstParam) { this.gstNumber = gstParam; }
    const emailsParam = params.get('emails');
    if (emailsParam) { this.emailsText = emailsParam; }
    const cityIdParam = params.get('cityId');
    if (cityIdParam) { this.cityId = cityIdParam; }

    this.loadCities();
    this.loadModes();

    // Fetch PAN from KYC once to set status; start polling only when on Documents section
    this.fetchPanFromKyc();

    // Fetch latest fleet info from server on initial load
    this.refreshFleetInfoFromMobile();
  }

  ngOnDestroy() {
    if (this.kycPollTimer) {
      clearInterval(this.kycPollTimer);
      this.kycPollTimer = null;
    }
  }

  private mapPanStatusToUiStatus(pan: any): string {
    try {
      const rawStatus: string = String(
        (pan && (pan.status || pan.verificationStatus || (pan.meta && pan.meta.status))) || ''
      ).toLowerCase();
      const verified: boolean = !!(pan && (pan.verified === true || (pan.meta && pan.meta.verified === true)));
      if (verified || ['approved', 'accepted', 'verified', 'success', 'ok'].includes(rawStatus)) {
        return 'approved';
      }
      if (['rejected', 'failed', 'error', 'declined'].includes(rawStatus)) {
        return 'rejected';
      }
      if (['processing', 'in_progress', 'pending', 'queued', 'review', 'submitted'].includes(rawStatus)) {
        return 'processing';
      }
    } catch (_e) {}
    return '';
  }

  private fetchPanFromKyc() {
    const userIdForKyc = this.fleetId; // use fleetId as userId for KYC
    this.onboardingService.getDocuments(userIdForKyc).subscribe((docs: any) => {
      const pan = docs && docs.data && docs.data.pan;
      const number = pan && (pan.number || (pan.meta && pan.meta.number));
      // pan.link could be a filestore path or data URL. We prefer to convert filestore path via presigned static URL
      const rawLink = pan && (pan.link || (pan.data && pan.data.link));
      // Set status from documents API
      this.documentsStatus = this.mapPanStatusToUiStatus(pan);
      // Show Approve only when PAN is in processing; hide when approved or otherwise
      this.canApprovePan = (this.documentsStatus === 'processing');
      if (typeof number === 'string') {
        this.fleetPan.number = number;
      }
      if (typeof rawLink === 'string' && rawLink.length > 0) {
        // If the link looks like a filestore url/path, extract the file name and hit static file api with fleet bucket
        try {
          const name = decodeURIComponent(rawLink.split('/').pop());
          if (name && name.indexOf('.') > 0) {
            // Replace captain bucket with fleet bucket as per requirement
            const imageBucket = 'fleet-onboarding-document-pancard';
            const type = 'image';
            this.onboardingService.getStaticFileUrl(imageBucket, type, name).subscribe((res: any) => {
              if (res && res.url) {
                this.fleetPanBase64 = res.url;
                this.stopKycPanPolling();
              } else {
                // Fallback to original link if presigned failed
                this.fleetPanBase64 = rawLink;
                this.stopKycPanPolling();
              }
            }, _e => {
              this.fleetPanBase64 = rawLink;
              this.stopKycPanPolling();
            });
          } else {
            // If it is already a data URL or cannot parse, just use it
            this.fleetPanBase64 = rawLink;
            this.stopKycPanPolling();
          }
        } catch (_e) {
          this.fleetPanBase64 = rawLink;
          this.stopKycPanPolling();
        }
      }
    }, _err => {});
  }

  private refreshDocumentsStatus() {
    if (!this.fleetId) { return; }
    this.onboardingService.getDocuments(this.fleetId).subscribe((docs: any) => {
      const pan = docs && docs.data && docs.data.pan;
      this.documentsStatus = this.mapPanStatusToUiStatus(pan);
      // Show Approve only when PAN is in processing; hide when approved or otherwise
      this.canApprovePan = (this.documentsStatus === 'processing');
    }, _err => {});
  }

  private refreshFleetInfoFromMobile() {
    if (!this.mobile) { return; }
    this.onboardingService.getFleetByMobile(this.mobile).subscribe((resp: any) => {
      const list = resp && resp.data ? resp.data : [];
      if (Array.isArray(list) && list.length > 0) {
        const fleet = list.find((f: any) => f && f._id === this.fleetId) || list[0];
        if (fleet) {
          this.name = fleet.name || this.name;
          this.gstNumber = fleet.gstNumber || this.gstNumber;
          const emails = Array.isArray(fleet.email) ? fleet.email.join(', ') : (fleet.email || '');
          this.emailsText = emails || this.emailsText;
          const cityId = Array.isArray(fleet.cities) && fleet.cities.length > 0 ? fleet.cities[0] : '';
          this.cityId = cityId || this.cityId;
          this.type = fleet.type === 'thirdParty' ? 'third_party' : (fleet.type || this.type);
          // Keep local status in sync with backend
          if (fleet.status) {
            this.status = String(fleet.status).trim().toLowerCase();
          }
          this.modeId = fleet.modeId || this.modeId;
          // If API returned all required fields, consider Info as complete/saved
          const hasAllInfo = !!(
            this.name && String(this.name).trim().length > 0 &&
            this.gstNumber && String(this.gstNumber).trim().length > 0 &&
            this.emailsText && String(this.emailsText).trim().length > 0 &&
            this.type && String(this.type).trim().length > 0 &&
            this.modeId && String(this.modeId).trim().length > 0 &&
            this.cityId && String(this.cityId).trim().length > 0
          );
          if (hasAllInfo) {
            this.infoSaved = true;
          }
        }
      }
    }, _err => {});
  }

  private kycPollTimer: any = null;
  private kycPollAttempts = 0;
  private maxKycPollAttempts = 20;

  private startKycPanPolling() {
    if (this.kycPollTimer) { return; }
    this.kycPollAttempts = 0;
    this.kycPollTimer = setInterval(() => {
      if (this.fleetPanBase64) {
        this.stopKycPanPolling();
        return;
      }
      this.kycPollAttempts += 1;
      if (this.kycPollAttempts > this.maxKycPollAttempts) {
        this.stopKycPanPolling();
        return;
      }
      this.fetchPanFromKyc();
    }, 3000);
  }

  private stopKycPanPolling() {
    if (this.kycPollTimer) {
      clearInterval(this.kycPollTimer);
      this.kycPollTimer = null;
    }
  }

  loadCities() {
    this.onboardingService.getCitiesList().subscribe(
      (resp: any) => {
        this.citiesList = resp && resp.geolayers ? resp.geolayers : [];
      },
      _err => {}
    );
  }

  loadModes() {
    try {
      const userId = this.fleetId || 'unknown';
      this.onboardingService.getModes(userId).subscribe((resp: any) => {
        const modes = resp && resp.data && resp.data.modes && resp.data.modes.data ? resp.data.modes.data : [];
        if (Array.isArray(modes) && modes.length > 0) {
          const loaded = modes.map((m: any) => ({ id: m._id, name: m.mode }));
          // If preselected modeId is set but not present in loaded list, append a placeholder
          const hasPreselected = this.modeId && loaded.some(opt => opt.id === this.modeId);
          this.modeOptions = hasPreselected ? loaded : (this.modeId ? [{ id: this.modeId, name: 'Selected mode' }, ...loaded] : loaded);
        }
      });
    } catch (_e) {}
  }

  isFormValid() {
    return !!(
      this.name &&
      this.emailsText &&
      this.type &&
      this.modeId &&
      this.cityId &&
      this.areEmailsAllValid(this.emailsText)
    );
  }

  get isInfoComplete(): boolean { return this.infoSaved; }

  get isNameMissing(): boolean {
    return !(this.name && String(this.name).trim().length > 0);
  }

  get isGstMissing(): boolean {
    return !(this.gstNumber && String(this.gstNumber).trim().length > 0);
  }

  get isCityMissing(): boolean {
    return !(this.cityId && String(this.cityId).trim().length > 0);
  }

  get isTypeMissing(): boolean {
    return !(this.type && String(this.type).trim().length > 0);
  }

  get isModeMissing(): boolean {
    return !(this.modeId && String(this.modeId).trim().length > 0);
  }

  get isEmailsMissing(): boolean {
    return !(this.emailsText && String(this.emailsText).trim().length > 0);
  }

  // Email validators for comma/space separated list
  private areEmailsAllValid(value: string): boolean {
    const text = String(value || '').trim();
    if (!text) { return false; }
    const parts = text.split(/[\,\s]+/).map(v => v.trim()).filter(v => !!v);
    if (parts.length === 0) { return false; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return parts.every(v => emailRegex.test(v));
  }

  get areEmailsInvalid(): boolean {
    const text = String(this.emailsText || '').trim();
    if (!text) { return false; }
    return !this.areEmailsAllValid(text);
  }

  goBack() {
    this.router.navigate(['/fleetOnboardingStart']);
  }

  switchSection(section: 'info' | 'documents') {
    // Block navigating to documents until info section is complete per current validation logic
    if (section === 'documents' && !this.isInfoComplete) {
      this.toastMessage.openSnackBarError('Please complete the Info section before proceeding', 2000);
      return;
    }
    this.activeSection = section;
    // KYC polling disabled; we rely on explicit fetch calls only
  }

  // Using existing app-pan component for PAN UI/validation

  private lastPanImageLink: string = '';
  private hasUploadedToKyc: boolean = false;

  onPanImage(link: string) {
    if (typeof link === 'string' && link.length > 0) {
      this.lastPanImageLink = link;
    }
  }

  async onPanMetaChange() {
    await this.onPanFetchData();
  }

  async onPanFetchData() {
    // Called after image update; fetch latest PAN number and upload to KYC if available
    if (!this.riderId || (this.fleetId && this.riderId === this.fleetId)) { return; }
    try {
      const res: any = await this.onboardingService.getRiderDetails(this.riderId || this.fleetId).toPromise();
      const data = res && res.data ? res.data : {};
      const panNumber: string = (data && data.panCard && data.panCard.number) || '';
      const link = this.lastPanImageLink || (data && data.panCard && data.panCard.link) || '';
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
      if (panNumber && panRegex.test(panNumber) && link && !this.hasUploadedToKyc) {
        await this.panService.uploadFleetPan({ riderId: this.riderId || this.fleetId, fleetId: this.fleetId, number: panNumber, link }).toPromise();
        this.hasUploadedToKyc = true;
        this.successMessage = 'PAN sent to KYC';
      }
    } catch (_e) { /* ignore fetch failures */ }
  }

  // Fleet PAN (KYC only) UI state
  fleetPan: { number?: string; link?: string; error?: string } = {};
  private fleetPanBase64: string = '';
  private fleetPanRotateDeg: number = 0;
  fleetPanLoading: boolean = false;
  canApprovePan: boolean = false;

  // Inline regex helper for template to show validation hint when typing PAN number
  get isFleetPanRegexInvalid(): boolean {
    const value = this.fleetPan && this.fleetPan.number ? this.fleetPan.number : '';
    if (!value) { return false; }
    return !this.isPanFormatValid(value);
  }

  private isPanFormatValid(value: string): boolean {
    const sanitized = String(value || '')
      .toUpperCase()
      .replace(/\s+/g, '');
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return !!sanitized && panRegex.test(sanitized);
  }

  onFleetPanFile(event: any) {
    const files = event && event.target && event.target.files ? event.target.files : null;
    const file = files && files.length ? files[0] : null;
    if (!file) { return; }
    const reader = new FileReader();
    reader.onload = () => {
      this.fleetPanBase64 = String((reader && reader.result) ? reader.result : '');
      this.fleetPan.error = '';
      this.fleetPanRotateDeg = 0;
      this.canApprovePan = false;
    };
    reader.readAsDataURL(file);
  }

  onFleetPanInputChange() {
    this.canApprovePan = false;
  }

  isFleetPanValid(): boolean {
    return !!(this.isPanFormatValid(this.fleetPan.number || '') && this.fleetPanBase64);
  }

  rotateFleetPan(direction: 'left' | 'right') {
    this.fleetPanRotateDeg += direction === 'left' ? -90 : 90;
  }

  async submitFleetPanKyc() {
    this.fleetPan.error = '';
    // Avoid using inline success for this flow; use toast instead
    if (!this.fleetPanBase64) { this.fleetPan.error = 'Please choose a PAN image'; return; }
    if (!this.isPanFormatValid(this.fleetPan.number || '')) {
      this.fleetPan.error = 'PAN must be 10 characters (e.g. CVCPK2345K) with no special characters';
      return;
    }
    try {
      this.fleetPanLoading = true;
      // Send data URL directly to KYC fleet upload endpoint (server will handle data URL)
      const link = this.fleetPanBase64;
      await this.panService.uploadFleetPan({ fleetId: this.fleetId, number: this.fleetPan.number, link }).toPromise();
      this.toastMessage.openSnackBarSuccess('PAN updated successfully');
      // PAN data changed → treat as fresh flow until re-verified
      this.documentsStatus = 'processing';
      this.canApprovePan = true;
      this.refreshDocumentsStatus();
      this.fetchPanFromKyc();
    } catch (e) {
      const httpErr: any = e || {};
      let backend: any = (httpErr.error !== undefined && httpErr.error !== null) ? httpErr.error : httpErr;
      if (typeof backend === 'string') { try { backend = JSON.parse(backend); } catch { } }
      const msg = (backend && (backend.message || (backend.info && backend.info.message))) || httpErr.message || 'KYC upload failed';
      this.toastMessage.openSnackBarError(typeof msg === 'string' ? msg : 'KYC upload failed', 2000);
      this.canApprovePan = false;
      this.refreshDocumentsStatus();
    } finally {
      this.fleetPanLoading = false;
    }
  }

  async approveFleetPan() {
    this.fleetPan.error = '';
    // Avoid using inline success for this flow; use toast instead
    try {
      this.fleetPanLoading = true;
      await this.panService.updateVerified(this.fleetId, 'true').toPromise();
      this.toastMessage.openSnackBarSuccess('PAN approved');
      this.documentsStatus = 'approved';
      this.canApprovePan = false;
      this.refreshDocumentsStatus();
    } catch (e) {
      const httpErr: any = e || {};
      let backend: any = (httpErr.error !== undefined && httpErr.error !== null) ? httpErr.error : httpErr;
      if (typeof backend === 'string') { try { backend = JSON.parse(backend); } catch { }
      }
      const msg = (backend && (backend.message || (backend.info && backend.info.message))) || httpErr.message || 'Approve failed';
      this.toastMessage.openSnackBarError(typeof msg === 'string' ? msg : 'Approve failed', 2000);
      this.refreshDocumentsStatus();
    } finally {
      this.fleetPanLoading = false;
    }
  }

  async activateFleet() {
    this.errorMessage = '';
    this.successMessage = '';
    try {
      this.loading = true;
      await this.onboardingService.activateFleet(this.fleetId).toPromise();
      this.toastMessage.openSnackBarSuccess('Fleet activated successfully');
      this.status = 'active';
    } catch (err) {
      const httpErr: any = err || {};
      let backend: any = (httpErr.error !== undefined && httpErr.error !== null) ? httpErr.error : httpErr;
    
      if (typeof backend === 'string') {
        try { backend = JSON.parse(backend); } catch { /* keep as string */ }
      }
    
      if (typeof backend === 'string') {
        this.toastMessage.openSnackBarError(backend, 2000);
      } else {
        const msg = backend.message || httpErr.message || 'Activation failed';
        const desc = backend.description || backend.desc; // <── DIRECT mapping to backend
        const combined = desc ? `${msg}: ${desc}` : msg;
        this.toastMessage.openSnackBarError(combined, 2000);
      }
    } finally {
      this.loading = false;
    }
  }

  async update() {
    this.errorMessage = '';
    this.successMessage = '';
    if (!this.isFormValid()) { return; }
    this.loading = true;
    try {
      const emails = this.emailsText.split(/[\s,]+/).map(e => e.trim()).filter(e => !!e);
      const payload: any = {
        source: 'supply-dashboard',
        name: this.name,
        dob: this.dob || undefined,
        gstNumber: this.gstNumber,
        cities: [this.cityId],
        email: emails,
        type: this.type === 'third_party' ? 'thirdParty' : this.type,
        modeId: this.modeId
      };
      // If modeId is selected from fresh modes list, it will already be an ObjectId string
      await this.onboardingService.updateFleet(this.fleetId, payload).toPromise();
      this.toastMessage.openSnackBarSuccess('Updated successfully');
      this.infoSaved = true;
      this.refreshFleetInfoFromMobile();
    } catch (err) {
      const httpErr: any = err || {};
      let backend: any = (httpErr.error !== undefined && httpErr.error !== null) ? httpErr.error : err;
      if (typeof backend === 'string') {
        try { backend = JSON.parse(backend); } catch { /* noop */ }
      }
      const msg = (backend && (backend.message || (backend.info && backend.info.message))) || httpErr.message || 'Update failed';
      this.toastMessage.openSnackBarError(typeof msg === 'string' ? msg : 'Update failed', 2000);
    } finally {
      this.loading = false;
    }
  }
} 